import type { SVGProps } from 'react';

export type IconProps = SVGProps<SVGSVGElement>;

function base(props: IconProps) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    'aria-hidden': true,
    ...props,
  } as const;
}

export function CheckIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="2">
      <path d="m5 12 4.5 4.5L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function LockIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.6">
      <rect x="5" y="10.5" width="14" height="9.5" rx="2.5" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" strokeLinecap="round" />
    </svg>
  );
}

export function ArrowRightIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.8">
      <path d="M4.5 12h15m-5.5-5.5L19.5 12 14 17.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PlusIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="2">
      <path d="M12 6v12M6 12h12" strokeLinecap="round" />
    </svg>
  );
}

export function MinusIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="2">
      <path d="M6 12h12" strokeLinecap="round" />
    </svg>
  );
}

export function PersonIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.7">
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 19.2c0-3.2 2.9-5.6 6.5-5.6s6.5 2.4 6.5 5.6" strokeLinecap="round" />
    </svg>
  );
}

export function SearchIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.8">
      <circle cx="10.5" cy="10.5" r="5.5" />
      <path d="m15 15 4.5 4.5" strokeLinecap="round" />
    </svg>
  );
}

export function StepsIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.8">
      <path d="M5 17.5h4V13h4V8.5h4V4" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="5" cy="17.5" r="1.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ShieldIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.8">
      <path d="M12 3.5 19 6.6v5.1c0 4.2-2.9 7.3-7 9.3-4.1-2-7-5.1-7-9.3V6.6L12 3.5Z" />
      <path d="m9 12 2.2 2.2L15.5 10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BellIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.8">
      <path d="M6.5 9.8a5.5 5.5 0 0 1 11 0c0 3.1.9 4.7 1.7 5.6H4.8c.8-.9 1.7-2.5 1.7-5.6Z" strokeLinejoin="round" />
      <path d="M10 18.5a2 2 0 0 0 4 0" strokeLinecap="round" />
    </svg>
  );
}

export function MailIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.7">
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
      <path d="m4.5 7 7.5 6 7.5-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function MenuIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="2">
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

export function CloseIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="2">
      <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
    </svg>
  );
}

export function AttachIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.7">
      <path
        d="M8.5 12.5 14 7a3.2 3.2 0 0 1 4.5 4.5l-6.8 6.8a5 5 0 0 1-7-7l6.6-6.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SendIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.8">
      <path d="M4.5 11.5 20 4.5l-4 15.5-4.2-5.2-7.3-3.3Z" strokeLinejoin="round" />
      <path d="m11.8 14.8 3.2-3.3" strokeLinecap="round" />
    </svg>
  );
}

export function TelegramIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} fill="currentColor">
      <path d="M20.7 4.3 3.9 10.8c-1 .4-1 1 .1 1.3l4.2 1.3 1.6 5c.2.6.4.8.8.8.4 0 .6-.2.9-.5l2.2-2.1 4.4 3.2c.8.4 1.3.2 1.5-.7l2.7-12.7c.3-1.1-.4-1.6-1.6-1.1ZM8.7 13.3l9.2-5.8c.4-.3.8-.1.5.2l-7.7 7-.3 3.1-1.7-4.5Z" />
    </svg>
  );
}

export function PhoneIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="1.7">
      <path
        d="M7 3.8 9.2 8l-1.8 1.8a12 12 0 0 0 6.8 6.8L16 14.8l4.2 2.2c.5.3.7.8.5 1.3l-.9 2.2c-.2.5-.7.8-1.2.8C10.6 21 3 13.4 3 5.4c0-.5.3-1 .8-1.2l2.2-.9c.5-.2 1 0 1.3.5Z"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CheckCircleIcon({ className = '', ...props }: IconProps) {
  return (
    <svg {...base(props)} className={className} stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" fill="currentColor" fillOpacity="0.16" stroke="none" />
      <path d="m7.8 12.2 2.7 2.7 5.7-5.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
